/**
 * Rule-of-thumb job pricing used by the built-in advisor and for instant suggestions in the
 * app (e.g. during onboarding). The Claude advisor gives richer, family-specific advice.
 */
import { JOB_TEMPLATES } from "./templates";

/** Fair hourly pay (in dollars) for household jobs by age. */
export function hourlyRate(age: number | null): number {
  if (age === null) return 5;
  if (age <= 6) return 2;
  if (age <= 9) return 4;
  if (age <= 12) return 6;
  if (age <= 15) return 8;
  return 10;
}

const MINUTE_HINTS: [RegExp, number, number][] = [
  [/mow|lawn/i, 60, 3],
  [/garage|attic|basement/i, 60, 2],
  [/car|wash.*car/i, 45, 3],
  [/snow|shovel/i, 45, 3],
  [/rake|leaves/i, 60, 3],
  [/babysit|watch.*(brother|sister|sibling)/i, 60, 2],
  [/cook|dinner|meal|bake/i, 40, 2],
  [/window/i, 30, 2],
  [/weed|garden|plant/i, 30, 2],
  [/bathroom|toilet|shower|tub/i, 20, 2],
  [/vacuum|mop|sweep/i, 20, 2],
  [/laundry|fold|iron/i, 20, 1],
  [/dog|walk/i, 20, 1],
  [/dish|dishwasher/i, 15, 1],
  [/trash|garbage|recycl/i, 10, 1],
  [/read/i, 20, 1],
  [/clean|tidy|organi[sz]e/i, 30, 2],
];

export function estimateJob(title: string, description?: string | null): { minutes: number; effort: 1 | 2 | 3 } {
  const text = `${title} ${description ?? ""}`;
  const template = JOB_TEMPLATES.find((t) => t.title.toLowerCase() === title.trim().toLowerCase());
  if (template) return { minutes: template.minutes, effort: template.effort };
  for (const [re, minutes, effort] of MINUTE_HINTS) if (re.test(text)) return { minutes, effort: effort as 1 | 2 | 3 };
  return { minutes: 20, effort: 2 };
}

export function niceRound(points: number): number {
  if (points <= 10) return Math.max(1, Math.round(points));
  if (points <= 50) return Math.round(points / 5) * 5;
  if (points <= 500) return Math.round(points / 10) * 10;
  return Math.round(points / 50) * 50;
}

export function quickPrice(
  fam: { pointsPerDollar: number; currencyName: string },
  job: { title: string; description?: string | null; estimatedMinutes?: number | null; age?: number | null },
) {
  const est = estimateJob(job.title, job.description);
  const minutes = job.estimatedMinutes ?? est.minutes;
  const effortMultiplier = est.effort === 1 ? 0.8 : est.effort === 3 ? 1.3 : 1;
  const dollars = (hourlyRate(job.age ?? null) * minutes * effortMultiplier) / 60;
  const points = Math.max(1, niceRound(dollars * fam.pointsPerDollar));
  const low = Math.max(1, niceRound(points * 0.75));
  const high = Math.max(points, niceRound(points * 1.3));
  const ageText = job.age ? ` for a ${job.age}-year-old` : "";
  return {
    suggestedReward: points,
    low,
    high,
    estimatedMinutes: minutes,
    rationale: `About ${minutes} minutes of ${est.effort === 3 ? "hard" : est.effort === 1 ? "light" : "moderate"} work${ageText}. At roughly $${hourlyRate(job.age ?? null)}/hour that's about $${dollars.toFixed(2)}, or ${points} ${fam.currencyName}.`,
  };
}
